/**
 * Real-Time Recording Modal
 *
 * Full-screen modal for real-time voice recording with live transcription display.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  Paper,
  Alert,
  IconButton,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Mic as MicIcon,
  Stop as StopIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  FiberManualRecord as RecordingIcon
} from '@mui/icons-material';
import { useWebSocketRecorder } from '../../hooks/useWebSocketRecorder';
import toast from 'react-hot-toast';

interface RealTimeRecordingModalProps {
  open: boolean;
  meetingId: number;
  meetingTitle: string;
  language?: string;
  onClose: () => void;
  onComplete: (meetingTextId: number) => void;
}

export const RealTimeRecordingModal: React.FC<RealTimeRecordingModalProps> = ({
  open,
  meetingId,
  meetingTitle,
  language = 'he',
  onClose,
  onComplete
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [newText, setNewText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [completedTextId, setCompletedTextId] = useState<number | null>(null);

  const {
    status,
    duration,
    transcriptParts,
    fullTranscript,
    connect,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reset,
    requestMicrophonePermission
  } = useWebSocketRecorder({
    meetingId,
    language,
    onTranscriptionUpdate: (text, confidence) => {
      // Highlight new text briefly
      setNewText(text);
      setTimeout(() => setNewText(''), 2000);

      // Show toast for low confidence
      if (confidence < 0.7) {
        toast('Low confidence transcription - may contain errors', {
          icon: '⚠️',
          duration: 2000
        });
      }
    },
    onError: (err) => {
      console.error('Recording error:', err);
      setError(err.message);
      toast.error(err.message);
    },
    onStatusChange: (newStatus) => {
      console.log('Recording status:', newStatus);

      if (newStatus === 'stopped') {
        // Check if we received a completion message with meeting_text_id
        // This will be set by the transcription_complete handler
      }
    }
  });

  // Auto-connect when modal opens
  useEffect(() => {
    if (open && status === 'idle') {
      connect();
    }
  }, [open, status, connect]);

  // Reset when modal closes
  useEffect(() => {
    if (!open && status !== 'idle') {
      console.log('[MODAL] Modal closed, resetting recorder state');
      reset();
      setError(null);
      setCompletedTextId(null);
      setNewText('');
    }
  }, [open, status, reset]);

  // Handle start recording
  const handleStart = async () => {
    setError(null);
    try {
      await startRecording();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // Handle pause
  const handlePause = () => {
    pauseRecording();
  };

  // Handle resume
  const handleResume = () => {
    resumeRecording();
  };

  // Handle stop
  const handleStop = async (save: boolean) => {
    await stopRecording(save);

    if (save && completedTextId) {
      onComplete(completedTextId);
    } else if (save) {
      // Even without completedTextId, refresh the page
      toast.success('Transcript saved successfully');
      onComplete(0); // Trigger refresh
    }

    onClose();
  };

  // Handle permission request
  const handleRequestPermission = async () => {
    try {
      const granted = await requestMicrophonePermission();
      if (granted) {
        toast.success('Microphone permission granted!');
        setError(null);
      } else {
        toast.error('Microphone permission denied. Please check your browser settings.');
      }
    } catch (err) {
      console.error('Permission request error:', err);
      toast.error('Failed to request microphone permission');
    }
  };

  // Format duration (MM:SS)
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Status chip color
  const getStatusColor = (): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (status) {
      case 'connected': return 'success';
      case 'recording': return 'error';
      case 'paused': return 'warning';
      case 'error': return 'error';
      default: return 'default';
    }
  };

  // Status text
  const getStatusText = () => {
    switch (status) {
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'recording': return 'Recording...';
      case 'paused': return 'Paused';
      case 'stopping': return 'Stopping...';
      case 'stopped': return 'Stopped';
      case 'error': return 'Error';
      default: return 'Idle';
    }
  };

  return (
    <Dialog
      open={open}
      fullScreen={isMobile}
      maxWidth="md"
      fullWidth
      onClose={() => handleStop(false)}
    >
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <MicIcon color={status === 'recording' ? 'error' : 'inherit'} />
            <Box>
              <Typography variant="h6">
                Real-Time Recording
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {meetingTitle}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            {status === 'recording' && (
              <RecordingIcon
                color="error"
                sx={{
                  animation: 'pulse 1.5s ease-in-out infinite',
                  '@keyframes pulse': {
                    '0%, 100%': { opacity: 1 },
                    '50%': { opacity: 0.3 }
                  }
                }}
              />
            )}
            <Chip
              label={getStatusText()}
              color={getStatusColor()}
              size="small"
            />
            <IconButton onClick={() => handleStop(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            onClose={() => setError(null)}
            action={
              error.toLowerCase().includes('permission') ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleRequestPermission}
                  variant="outlined"
                  sx={{ ml: 1 }}
                >
                  Request Permission
                </Button>
              ) : null
            }
          >
            <Box>
              <Typography variant="body2" component="div">
                {error.split('\n').map((line, index) => (
                  <div key={index}>{line}</div>
                ))}
              </Typography>
            </Box>
          </Alert>
        )}

        {status === 'connecting' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Connecting to server... Please wait
          </Alert>
        )}

        {/* Timer */}
        <Box textAlign="center" my={3}>
          <Typography
            variant="h2"
            fontFamily="monospace"
            color={status === 'recording' ? 'error.main' : 'text.primary'}
          >
            {formatDuration(duration)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {transcriptParts.length} parts transcribed
          </Typography>
        </Box>

        {/* Recording Controls */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          justifyContent="center"
          my={3}
        >
          {status === 'connected' && (
            <Button
              variant="contained"
              color="error"
              size="large"
              startIcon={<MicIcon />}
              onClick={handleStart}
              fullWidth={isMobile}
            >
              Start Recording
            </Button>
          )}

          {status === 'recording' && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<PauseIcon />}
              onClick={handlePause}
              fullWidth={isMobile}
            >
              Pause
            </Button>
          )}

          {status === 'paused' && (
            <Button
              variant="outlined"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleResume}
              fullWidth={isMobile}
            >
              Resume
            </Button>
          )}

          {(status === 'recording' || status === 'paused') && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              startIcon={<StopIcon />}
              onClick={() => handleStop(true)}
              fullWidth={isMobile}
            >
              Stop & Save
            </Button>
          )}
        </Stack>

        {/* Live Transcript Display */}
        <Paper
          elevation={2}
          sx={{
            p: 3,
            minHeight: 300,
            maxHeight: 400,
            overflow: 'auto',
            direction: 'rtl',
            textAlign: 'right',
            backgroundColor: theme.palette.mode === 'dark' ? 'grey.900' : 'grey.50'
          }}
        >
          <Typography variant="subtitle2" gutterBottom color="text.secondary">
            Live Transcription:
          </Typography>

          {transcriptParts.length === 0 && status !== 'stopped' && (
            <Typography color="text.secondary" fontStyle="italic">
              Live transcription will appear here...
            </Typography>
          )}

          {transcriptParts.length === 0 && status === 'stopped' && (
            <Typography color="text.secondary">
              No transcription received
            </Typography>
          )}

          {transcriptParts.map((part, index) => (
            <Typography
              key={index}
              component="span"
              sx={{
                display: 'inline',
                backgroundColor: part.text === newText ? 'yellow' : 'transparent',
                transition: 'background-color 0.5s ease-out',
                padding: '2px 4px',
                borderRadius: '4px'
              }}
            >
              {part.text}{' '}
            </Typography>
          ))}
        </Paper>

        {/* Word Count & Confidence */}
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          justifyContent="center"
          alignItems="center"
          mt={2}
        >
          <Typography variant="body2" color="text.secondary">
            <strong>Words:</strong> {fullTranscript.split(' ').filter(w => w.trim()).length}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Average Confidence:</strong>{' '}
            {transcriptParts.length > 0
              ? Math.round(
                  transcriptParts.reduce((sum, p) => sum + p.confidence, 0) /
                  transcriptParts.length * 100
                ) + '%'
              : '—'}
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => handleStop(false)}
          disabled={status === 'stopping'}
          color="inherit"
        >
          Cancel & Discard
        </Button>
        <Button
          onClick={() => handleStop(true)}
          variant="contained"
          startIcon={<CheckIcon />}
          disabled={status !== 'recording' && status !== 'paused' && status !== 'stopped'}
        >
          {status === 'stopped' ? 'Close' : 'Save Transcript'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
